import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import { getStageTypeDisplay, getStageTimeLabel, getStandCellInfo } from '../../lib/conventionResults';
import { findDiplomaPlacement } from '../../lib/conventionDiploma';

// A per-stand result row is "shot" when it carries any score component —
// mirrors the same gate in resultsTable.js so not-yet-entered stands render
// blank here exactly like on the results page.
function standEntryHasScore(e) {
    if (!e) return false;
    return ((e.scoreHits || 0) + (e.scoreTargets || 0) + (e.scoreInner || 0) + (e.scorePenalty || 0)) > 0;
}

@observer
class ConventionMyPurchases extends Component {
    constructor(props) {
        super(props);
        this.state = {
            sales: [],
            loading: true,
        };
    }

    loadPurchases = async () => {
        const { saleStore, userStore } = this.props.stores;
        const { convention } = this.props;
        const { user } = userStore;

        if (!convention?.id || !user?.id) {
            this.setState({ loading: false });
            return;
        }

        // Only load sales where products have conventionId matching this convention (new flow).
        // Do NOT query by productsProductId (old flow) as it matches sales from other conventions
        // that use the same signup product, causing cross-convention contamination.
        const sales = await saleStore.load({
            query: {
                productsConventionId: convention.id,
                user: user.id,
                allCustomers: true,
            },
            skipUpdate: true,
        }) || [];

        this.setState({
            sales,
            loading: false,
        });
    }

    componentDidMount() {
        this.loadPurchases();
    }

    openAnalysis = (stageMd5) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { appState, userStore } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;
        appState.openDrawer('shooterDetail', {
            participantId: userStore.user?.id,
            stageMd5,
            height: drawerHeightMediumLarge,
        });
    }

    render() {
        const { sales, loading } = this.state;
        const { userStore, conventionStore, conventionResultStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        const { convention } = this.props;

        if (loading) {
            return (
                <div class='text-center py-2'>
                    <i class='fa-solid fa-spinner fa-spin' />
                </div>
            );
        }

        // Determine signup product payment status from the convention participant data.
        // This is the authoritative source (same as what "Deltakere" uses).
        const signupProductId = convention.signupProduct;
        const hasSignupProduct = signupProductId > 0;
        const participants = convention.participants || [];
        const currentParticipant = participants.find(p => p.id === user.id);
        const signupIsPaid = currentParticipant?.paymentStatus === 3;

        // Stages (lag) the current user is signed up for.
        const myStages = [];
        (convention.stages || []).forEach((stage, idx) => {
            const stageParticipants = convention[`participants${idx + 1}`] || [];
            if (stageParticipants.some(p => p.id === user.id)) {
                myStages.push(stage);
            }
        });

        // Placement (#) comes from the same open-bucket ranking the results
        // page and the diploma use, so all three always agree.
        const resultList = myStages.length > 0
            ? conventionResultStore.getResultsListForAllClasses(
                convention,
                conventionResultStore.conventionResults || [],
                conventionStore.participants || [],
                conventionStore.classMap,
                conventionStore.disciplineMap,
            )
            : {};

        // Collect other convention products from sales (these have conventionId set correctly)
        const otherProducts = [];
        sales.forEach(sale => {
            const products = sale.products || [];
            products.forEach(product => {
                if (product.conventionId === convention.id && product.id !== signupProductId) {
                    otherProducts.push({
                        ...product,
                        saleId: sale.id,
                        paidDate: sale.paidDate,
                    });
                }
            });
        });

        // Nothing to show if no signup product, no other products and no stages
        if (!hasSignupProduct && otherProducts.length === 0 && myStages.length === 0) {
            return null;
        }

        return (<>
            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mb-1 mt-3'>
                <span>
                    <i class='fa-duotone fa-clipboard-list me-2' />
                    <Text id='convention.my-convention'>My convention</Text>
                </span>
            </div>
            <div class={`mt-0 mb-3 mx-3 px-3 py-2 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                {myStages.length > 0 && (
                    <div class={`${(hasSignupProduct || otherProducts.length > 0) ? 'border-bottom mb-2 pb-2' : ''}`}>
                        {myStages.map(stage => {
                            const detail = conventionResultStore.getShooterDetail(
                                convention,
                                conventionResultStore.conventionResults || [],
                                conventionStore.participants || [],
                                { participantId: user.id, stageMd5: stage.md5 },
                                { classMap: conventionStore.classMap, disciplineMap: conventionStore.disciplineMap },
                            );
                            const hasScore = !!detail && detail.perStand.length > 0;

                            // Per-stand values, ± and placement mirror the results
                            // page scorecard row exactly (same helpers, same gates).
                            const standNumbers = [];
                            const standValues = {};
                            let lostTotal = 0;
                            let unknownMax = false;
                            let shotCount = 0;
                            let placement = null;
                            if (hasScore) {
                                for (let n = 1; n <= (detail.expectedStands || 1); n += 1) {
                                    standNumbers.push(n);
                                }
                                detail.perStand.forEach(perStand => {
                                    if (!standEntryHasScore(perStand.result)) return;
                                    standValues[perStand.stand] = perStand.value;
                                    shotCount += 1;
                                    const info = getStandCellInfo(perStand.result, perStand.stand, { type: stage.type }, convention, detail.weaponClass);
                                    if (!info || info.max == null) unknownMax = true;
                                    else lostTotal += info.lost;
                                });
                                placement = findDiplomaPlacement(resultList, { userId: user.id, stageMd5: stage.md5 })?.placement || null;
                            }
                            const showPlusMinus = shotCount > 0 && !unknownMax;

                            return (
                                <div class='py-1' key={stage.md5}>
                                    <div class='d-flex justify-content-between align-items-center'>
                                        <span>
                                            <i class='fa-duotone fa-flag-checkered me-2 text-primary' />
                                            {stage.name}
                                            <span class='text-body-secondary ms-1'>({getStageTypeDisplay(stage.type)})</span>
                                        </span>
                                        <span class='d-flex align-items-center'>
                                            {hasScore && (
                                                <button
                                                    type='button'
                                                    class='btn btn-sm btn-outline-primary rounded-pill px-2 py-0 me-2'
                                                    style='font-size: 0.75rem;'
                                                    onClick={this.openAnalysis(stage.md5)}
                                                >
                                                    <i class='fa-duotone fa-chart-column me-1' />
                                                    <Text id='convention.analysis-tab'>Analysis</Text>
                                                </button>
                                            )}
                                            <span class='text-body-secondary'>
                                                {getStageTimeLabel(stage, convention)}
                                            </span>
                                        </span>
                                    </div>
                                    {hasScore && (
                                        <div class='table-responsive'>
                                            <table class='table table-sm align-middle mb-1' style='font-size: 0.85rem;'>
                                                <thead>
                                                    <tr>
                                                        <th style='width: 40px;'>#</th>
                                                        <th><Text id='convention.results-name-club'>Name / club</Text></th>
                                                        {standNumbers.map(n => (
                                                            <th key={n} class='text-center text-body-secondary' style='width: 44px;'>{n}</th>
                                                        ))}
                                                        <th class='text-end' style='width: 50px;'>iX</th>
                                                        <th class='text-end' style='width: 60px;'><Text id='convention.results-sum'>Sum</Text></th>
                                                        <th class='text-end' style='width: 50px;'>±</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr
                                                        role='button'
                                                        style='cursor: pointer;'
                                                        onClick={this.openAnalysis(stage.md5)}
                                                    >
                                                        <td class='text-body-secondary'>{placement != null ? placement : ''}</td>
                                                        <td>
                                                            <div>{detail.name}</div>
                                                            {detail.sportsClub && (
                                                                <div class='small text-body-secondary'>{detail.sportsClub}</div>
                                                            )}
                                                        </td>
                                                        {standNumbers.map(n => (
                                                            <td key={n} class='text-center font-monospace'>
                                                                {standValues[n] != null ? standValues[n] : ''}
                                                            </td>
                                                        ))}
                                                        <td class='text-end text-body-secondary'>{detail.totalIx}</td>
                                                        <td class='text-end fw-bold'>{detail.totalScore}</td>
                                                        <td class='text-end'>
                                                            {showPlusMinus && (lostTotal === 0
                                                                ? <span class='text-success-emphasis fw-semibold'>E</span>
                                                                : <span class='text-danger'>−{lostTotal}</span>)}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                {hasSignupProduct && (
                    <div class='d-flex justify-content-between align-items-center py-1'>
                        <span>
                            <i class={`fa-solid fa-user-check me-2 ${signupIsPaid ? 'text-success' : 'text-warning'}`} />
                            {convention.signupProductName || <Text id='convention.signup-fee'>Signup fee</Text>}
                            {myStages.length > 1 && <span class='text-body-secondary ms-1'>x{myStages.length}</span>}
                        </span>
                        <span class={`badge ${signupIsPaid ? 'text-bg-success' : 'text-bg-warning'}`}>
                            {signupIsPaid
                                ? <Text id='convention.paid'>betalt</Text>
                                : <Text id='convention.not-paid'>not paid</Text>
                            }
                        </span>
                    </div>
                )}
                {otherProducts.map(product => {
                    const priceWithVat = (product.price || 0) * (1 + (product.vat || 0) / 100);
                    const isPaid = !!product.paidDate;
                    return (
                        <div class='d-flex justify-content-between align-items-center py-1' key={`${product.saleId}-${product.id}`}>
                            <span>
                                <i class={`fa-solid fa-box me-2 ${isPaid ? 'text-success' : 'text-warning'}`} />
                                {product.name}
                                {product.qty > 1 && <span class='text-body-secondary ms-1'>x{product.qty}</span>}
                            </span>
                            <span class='d-flex align-items-center gap-2'>
                                <span class='text-body-secondary'>
                                    {util.format(priceWithVat * (product.qty || 1), 0)} kr
                                </span>
                                <span class={`badge ${isPaid ? 'text-bg-success' : 'text-bg-warning'}`}>
                                    {isPaid
                                        ? <Text id='convention.paid'>betalt</Text>
                                        : <Text id='convention.not-paid'>not paid</Text>
                                    }
                                </span>
                            </span>
                        </div>
                    );
                })}
            </div>
        </>);
    }
}

export default ConventionMyPurchases;
